import React, { useRef, useState } from "react";
import { ArrowShapeUpFromLine } from "@gravity-ui/icons";
import { usePostHog } from "posthog-js/react";
import Toast from "../generic_components/Toast"; // If you need toasts within this modal
import { useLocalState } from "../../contexts/LocalStateProvider";

// Add Spinner component (simple example, replace with your preferred spinner)
const Spinner = () => (
  <div className="flex justify-center items-center h-full">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
  </div>
);

// This component defines the content structure for the file upload modal
const CustomPromptModal = ({
  title,
  description,
  fileTypes,
  handleFileChange, // Function passed from Page.js to handle file selection/drop
  closeModal,       // Function passed from Page.js to close the modal
  toastMessage,     // Prop to receive toast message state from Page.js (optional)
  setToastMessage,  // Prop to set toast message state in Page.js (optional)
}) => {
  const posthog = usePostHog();
  const { updateLocalState } = useLocalState();
  const dropRef = useRef(null);
  // <<< Add state for processing indicator >>>
  const [isProcessing, setIsProcessing] = useState(false); 

  const handleLocalFileChange = (event) => {
    const file = event.target.files ? event.target.files[0] : null;
    if (file) {
      posthog.capture("Click-UploadContent-CustomPromptModal", {
      is_user_logged_in: true,
      fileName: file.name,
      fileType: file.type,
      fileSizeKB: Math.round(file.size / 1024),
    });

      let contextName = title ? title.toLowerCase().trim() : "content";

      // Remove common prefixes
      const prefixes = ["upload ", "attach ", "add your ", "select your ", "choose your "];
      for (const prefix of prefixes) {
        if (contextName.startsWith(prefix)) {
          contextName = contextName.substring(prefix.length).trim();
          break; // Assuming only one such prefix is primary
        }
      }

      // Handle slashes (e.g., "Video/Audio" -> "Video", "Presentation Video/Audio" -> "Presentation Video")
      const slashIndex = contextName.indexOf('/');
      if (slashIndex !== -1) {
        contextName = contextName.substring(0, slashIndex).trim();
      }
      
      // Remove common type specifiers from the end if the name has multiple words
      // (e.g., "Presentation Video" -> "Presentation", but "Audio" remains "Audio")
      const words = contextName.split(' ');
      if (words.length > 1) {
          const lastWord = words[words.length - 1];
          const typeSpecifiers = ["video", "audio", "document", "image", "file", "content", "track", "picture", "clip", "recording"];
          if (typeSpecifiers.includes(lastWord)) {
              const potentialContextName = words.slice(0, -1).join(' ').trim();
               // Only update if the result is not empty, to preserve meaningful single-word contexts that might also be type specifiers
              if (potentialContextName) {
                contextName = potentialContextName;
              }
          }
      }
      
      // Final fallback if contextName became empty after processing
      if (!contextName) {
        contextName = "content"; 
      }

      const generatedText = `Create a track for the attached ${contextName}.`;
      if (updateLocalState) {
          updateLocalState("textPrompt", generatedText);
      }

      setIsProcessing(true); // <<< Set processing state to true
      handleFileChange(event); // Call the original handler passed from Page.js
    } else {
      console.error("No file selected");
       if(setToastMessage && typeof setToastMessage === 'function') {
           setToastMessage({ type: "error", message: "No file selected."});
       }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];

    if (droppedFile) {
      let contextName = title ? title.toLowerCase().trim() : "content";

      // Remove common prefixes
      const prefixes = ["upload ", "attach ", "add your ", "select your ", "choose your "];
      for (const prefix of prefixes) {
        if (contextName.startsWith(prefix)) {
          contextName = contextName.substring(prefix.length).trim();
          break; // Assuming only one such prefix is primary
        }
      }

      // Handle slashes (e.g., "Video/Audio" -> "Video", "Presentation Video/Audio" -> "Presentation Video")
      const slashIndex = contextName.indexOf('/');
      if (slashIndex !== -1) {
        contextName = contextName.substring(0, slashIndex).trim();
      }
      
      // Remove common type specifiers from the end if the name has multiple words
      // (e.g., "Presentation Video" -> "Presentation", but "Audio" remains "Audio")
      const words = contextName.split(' ');
      if (words.length > 1) {
          const lastWord = words[words.length - 1];
          const typeSpecifiers = ["video", "audio", "document", "image", "file", "content", "track", "picture", "clip", "recording"];
          if (typeSpecifiers.includes(lastWord)) {
              const potentialContextName = words.slice(0, -1).join(' ').trim();
               // Only update if the result is not empty, to preserve meaningful single-word contexts that might also be type specifiers
              if (potentialContextName) {
                contextName = potentialContextName;
              }
          }
      }
      
      // Final fallback if contextName became empty after processing
      if (!contextName) {
        contextName = "content"; 
      }

      const generatedText = `Create a track for the attached ${contextName}.`;
      if (updateLocalState) {
          updateLocalState("textPrompt", generatedText);
      }

      const fileEvent = {
        target: { files: [droppedFile] },
      };
      setIsProcessing(true); // <<< Set processing state to true
      // Call the handler passed from Page.js
      handleFileChange(fileEvent);
    } else {
      console.error("No file dropped");
       if(setToastMessage && typeof setToastMessage === 'function') {
           setToastMessage({ type: "error", message: "No file dropped."});
       }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

   // Flag for valid toast data passed in props
   const hasValidToast = toastMessage && typeof toastMessage === 'object' && typeof toastMessage.message === 'string';

  return (
    <div className="relative text-left bg-[#1d1b1d] border border-solid border-[#3c393c] rounded-lg w-full md:w-[32rem] self-center z-10">
      {/* Modal Title */}
      <h2 className="text-xl font-normal text-left w-full px-3 pt-3 pb-0">
        {title || "Upload Content"} {/* Add default title */}
      </h2>

      {/* Description Section */}
      {description && Array.isArray(description) && (
        <div className="px-3 py-3 text-sm text-[#C5C3C6] font-extralight">
           <p className="font-extralight mb-4">Here's what we do:</p>
           <ul className="list-disc list-inside ml-1">
             {description.map((item, index) => (
               <li key={index}>{item}</li>
             ))}
           </ul>
        </div>
      )}

      {/* Dropzone */}
      <div
        ref={dropRef}
        // Make height consistent whether showing dropzone or spinner
        className="border-dashed m-3 border-2 border-[#3c393c] p-4 rounded-lg bg-white/5 text-center flex items-center gap-3" 
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        {/* <<< Conditional Rendering for Loading State >>> */}
        {isProcessing ? (
          <Spinner /> // Show spinner when processing
        ) : (
          // Show original dropzone content when not processing
          <> 
            <ArrowShapeUpFromLine className="w-9 h-9 text-white" />
            <div className="flex w-full items-start flex-col">
              <div className="flex w-full items-center">
                <p className="text-base font-extralight">
                  Drop your content here or{" "}
                  <label
                    htmlFor="customPromptFileInput" 
                    className="text-white cursor-pointer font-normal underline"
                    onClick={(e) => {
                      posthog.capture("Click-UploadContent-CustomPromptModal", {
                        is_user_logged_in: true
                      });
                    }}
                  >
                    Browse
                  </label>
                </p>
                <input
                  type="file"
                  accept={fileTypes?.join(",") || "*/*"}
                  onChange={handleLocalFileChange} // <<< Use the local handler
                  className="hidden"
                  id="customPromptFileInput" 
                />
              </div>
              <div className="flex w-full items-center gap-2 ">
                <p className="text-gray-400 font-extralight text-sm">
                  File supported: {fileTypes?.map(ft => ft.split('/')[1] || ft).join(", ") || "any"} 
                </p>
                <p className="text-gray-400 font-extralight text-sm">File size: max 20 MB</p>
              </div>
            </div>
          </>
        )}
      </div>

       {/* Optional Toast Display within Modal */}
       {hasValidToast && (
        <Toast
          type={typeof toastMessage.type === 'string' ? toastMessage.type : "info"}
          message={toastMessage.message}
          onClose={setToastMessage && typeof setToastMessage === 'function' ? () => setToastMessage(null) : undefined}
        />
      )}
       {/* Note: The outer Modal component in Page.js handles the main close button */}
    </div>
  );
};

export default CustomPromptModal;
